import { Stack, useMantineTheme } from '@mantine/core';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DisplayCreative } from '@feathr/blackbox';
import { Heading, Tab, Tabs } from '@feathr/components';

import LinksTab from './tabs/LinksTab';
import SetupTab from './tabs/SetupTab';
import TargetingTab from './tabs/TargetingTab';

import * as styles from './CreativeForm.css';

type TTabId = 'setup' | 'links' | 'targeting';

interface ICreativeFormProps {
  creative: DisplayCreative;
  /** Campaign ID, used to filter CTA options by campaign objective. */
  campaignId?: string;
}

function CreativeForm({ creative, campaignId }: Readonly<ICreativeFormProps>): JSX.Element {
  const { t } = useTranslation();
  const theme = useMantineTheme();
  const [activeTab, setActiveTab] = useState<TTabId>('setup');

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Heading order={4} variant={'h4'}>
          {t('Edit Details')}
        </Heading>
      </div>
      <div className={styles.tabsWrapper}>
        <Tabs>
          <Tab
            active={activeTab === 'setup'}
            onClick={(): void => setActiveTab('setup')}
            title={t('Setup')}
          />
          <Tab
            active={activeTab === 'links'}
            onClick={(): void => setActiveTab('links')}
            title={t('Links')}
          />
          <Tab
            active={activeTab === 'targeting'}
            onClick={(): void => setActiveTab('targeting')}
            title={t('Targeting')}
          />
        </Tabs>
      </div>
      <div className={styles.body}>
        <Stack gap={theme.spacing['4']}>
          {activeTab === 'setup' && <SetupTab campaignId={campaignId} creative={creative} />}
          {activeTab === 'links' && <LinksTab creative={creative} />}
          {activeTab === 'targeting' && <TargetingTab creative={creative} />}
        </Stack>
      </div>
    </div>
  );
}

export default observer(CreativeForm);
